import * as React from "react";
import { DateTime } from "luxon";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import Table from "components/UI/Table";
import {
  billingReportsService,
  regenerateReportService,
  removeReportService,
  sendReportService,
} from "services/ReportService";
import { Report, ReportResponseType } from "types/Report";
import { useModal } from "components/UI/Modal/ModalProvider";
import MessageModal from "components/UI/Message/MessageModal";
import Loading from "components/UI/Modal/Loading";
import ReportManagementModal from "./ReportManagementModal";
import { resolveFileUploads } from "libs";
import ReportUpdateModal from "./ReportUpdateModal";

interface BillingReportListProps {}

const BillingReportList: React.FC<BillingReportListProps> = () => {
  const modalButtonRef = React.useRef<HTMLButtonElement>(null);
  const loadingModal = useModal(Loading);
  const reportUpdateModal = useModal(ReportUpdateModal);

  const history = useHistory();
  const messageModal = useModal(MessageModal);
  const generateReportModal = useModal(ReportManagementModal);
  const [limit, setLimit] = React.useState<number>(25);
  const [uiTimestamp, SetUiTimestamp] = React.useState<string>(
    DateTime.now().toISO()
  );

  const onGenerateReportHandler = async () => {
    const res = await generateReportModal();
    if (res?.data) {
      toast.success(res.data.message);
      history.push(res.data.url);
      SetUiTimestamp(DateTime.now().toISO());
    }
  };

  const onRemoveReportHandler = async (report: Report) => {
    const res = await messageModal({
      message: `Remover reporte ${report.placeGroup.name}?`,
      confirm: true,
    });
    if (res?.data) {
      const res2: any = await removeReportService({ uuid: report.uuid });

      if (res2?.data) {
        toast.success(res2?.data);
        SetUiTimestamp(DateTime.now().toISO());
      } else if (res2?.error) {
        toast.error(res2.error);
      }
    }
  };

  const openReportHandler = (report: Report) => {
    window.open(resolveFileUploads(report.documentUrl), "_blank");
  };

  const regeneratePDFHandler = async (report: Report) => {
    const res = await messageModal({
      message: `Regenerar reporte ${report.placeGroup.name}?`,
      confirm: true,
    });
    if (res?.data) {
      loadingModal({ modalButtonRef, message: "Regenerando reporte" });
      const res2: any = await regenerateReportService({ uuid: report.uuid });

      modalButtonRef.current.click();

      if (res2?.data) {
        toast.success(res2?.data);
        SetUiTimestamp(DateTime.now().toISO());
      } else if (res2?.error) {
        toast.error(res2.error);
      }
    }
  };

  const sendReportHandler = async (report: Report) => {
    const res = await messageModal({
      message: `Enviar reporte ${report.placeGroup.name}?`,
      confirm: true,
    });
    if (res?.data) {
      loadingModal({ modalButtonRef, message: "Enviando reporte" });
      const res2: any = await sendReportService({ uuid: report.uuid });

      modalButtonRef.current.click();

      if (res2?.data) {
        toast.success(res2?.data);
        SetUiTimestamp(DateTime.now().toISO());
      } else if (res2?.error) {
        toast.error(res2.error);
      }
    }
  };

  const onReportUpdateHanlder = async (report: Report) => {
    const result = await reportUpdateModal({ report });

    if (result?.data) {
      toast.success(result.data);
      SetUiTimestamp(DateTime.now().toISO());
    }
  };

  return (
    <div key={uiTimestamp} className="h-full w-full">
      <Table
        title="Lista de cobranza"
        limit={limit}
        columns={[
          { title: "Grupo", field: "placeGroup.name" },
          { title: "Transacciones", field: "orderCount" },
          {
            title: "Fecha de reporte",
            render: (rowData: Report) => (
              <div className="flex flex-row space-x-2">
                <span>
                  {DateTime.fromISO(rowData.fromDate).toFormat("dd/MM/yyyy")}
                </span>
                <span>-</span>
                <span>
                  {DateTime.fromISO(rowData.toDate).toFormat("dd/MM/yyyy")}
                </span>
              </div>
            ),
          },
          { title: "Creador", field: "user" },
          {
            title: "Creación",
            render: (rowData: Report) => (
              <span>
                {DateTime.fromISO(rowData.createdAt).toLocaleString(
                  DateTime.DATETIME_SHORT
                )}
              </span>
            ),
          },
          // {
          //   title: 'Pagado',
          //   render: (rowData: Report) => (<span>{rowData.paid ? 'SI' : 'NO'}</span>)
          // },
          {
            title: "Enviado",
            render: (rowData: Report) => (
              <span>{rowData.sent ? "SI" : "NO"}</span>
            ),
          },
        ]}
        data={async (query) => {
          const res = (await billingReportsService({
            input: { page: query.page + 1, query: query.search },
          })) as ReportResponseType;

          setLimit(res.totalDocs > res.limit ? res.limit : res.totalDocs);

          return {
            data: res.docs,
            page: res.page - 1,
            totalCount: res.totalDocs,
          };
        }}
        actions={[
          (_) => ({
            icon: "pageview",
            tooltip: "Previsualizar Reporte",
            onClick: (_, rowData: Report) => openReportHandler(rowData),
          }),
          (_) => ({
            icon: "picture_as_pdf",
            tooltip: "Generar PDF",
            onClick: (_, rowData: Report) => regeneratePDFHandler(rowData),
          }),
          (_) => ({
            icon: "edit",
            tooltip: "Editar detalles",
            onClick: (_, rowData: Report) => onReportUpdateHanlder(rowData),
          }),
          (props) =>
            props.paid && {
              icon: "send",
              tooltip: "Enviar Reporte",
              onClick: (_, rowData: Report) => sendReportHandler(rowData),
            },
          (_) => ({
            icon: "delete",
            tooltip: "Eliminar Reporte",
            onClick: (_, rowData: Report) => onRemoveReportHandler(rowData),
          }),
          {
            icon: "add",
            tooltip: "Generar reporte",
            isFreeAction: true,
            onClick: onGenerateReportHandler,
          },
        ]}
      />
    </div>
  );
};

export default BillingReportList;
